/**
 * Rescigo · Universal Weighing-Scale Reader (Iter 82)
 * --------------------------------------------------------------
 * One entry-point that streams weight from any of three transports:
 *
 *   1. Bluetooth Low Energy (Web Bluetooth API)
 *      - Standard SIG "Weight Scale Service" (0x181D) with the
 *        `Weight Measurement` characteristic (0x2A9D).
 *      - Falls back to *any* NOTIFY characteristic that dumps ASCII
 *        (many cheap 58 mm-priced Chinese scales do this).
 *
 *   2. USB-Serial (Web Serial API)
 *      - For old counter scales wired via a USB-serial adapter or a
 *        genuine COM port. Opens at 9600 8N1 (industry default).
 *
 *   3. USB HID scale (Web HID API, best-effort)
 *      - Standard USB HID Scale usage-page (0x8D). Some Baby / kitchen
 *        weight scales enumerate this way.
 *
 * Public API:
 *
 *   const stop = await startScaleStream({
 *     transport: "auto" | "ble" | "serial" | "hid",
 *     onReading:  ({kg, grams, unit, stable}) => void,
 *     onStatus:   (msg) => void,   // "connecting", "ready", ...
 *   });
 *   // …later:
 *   stop();
 *
 *   await pairScale("ble" | "serial" | "hid")   // one-time picker
 *   await disconnectScale()                     // release resources
 *
 * Persistence:
 *   localStorage[rescigo.scale.transport] — last successful transport
 *   localStorage[rescigo.scale.device]    — device name (BLE) or "COM"
 */

const LS = {
  transport: "rescigo.scale.transport",
  device:    "rescigo.scale.device",
  autoCap:   "rescigo.scale.auto_capture",   // "1" | "0"
  holdSec:   "rescigo.scale.hold_seconds",   // "3" | "5" | "10"
};

// SIG Weight Scale service + characteristic
const WEIGHT_SERVICE_UUID = "0000181d-0000-1000-8000-00805f9b34fb";
const WEIGHT_CHAR_UUID    = "00002a9d-0000-1000-8000-00805f9b34fb";

// Extra service UUIDs commonly exposed by cheap ASCII-notify scales.
// We add them to `optionalServices` so the browser gives us access
// after the user picks a device.
const EXTRA_SCALE_SERVICES = [
  "0000181b-0000-1000-8000-00805f9b34fb", // Body composition
  "0000fff0-0000-1000-8000-00805f9b34fb", // JDY / Nordic UART clones
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
  "0000ffe0-0000-1000-8000-00805f9b34fb", // HM-10 / BLE-UART
  "0000fee7-0000-1000-8000-00805f9b34fb", // Mi Scale
];

const dec = new TextDecoder();

// -------------------- persistence + capability ------------------
export function lastTransport() {
  return localStorage.getItem(LS.transport) || "";
}
export function lastDeviceLabel() {
  return localStorage.getItem(LS.device) || "";
}
/** Auto-capture preferences (persisted). */
export function getAutoCapture() {
  const v = localStorage.getItem(LS.autoCap);
  return v === null ? true : v === "1"; // default ON
}
export function setAutoCapture(on) {
  localStorage.setItem(LS.autoCap, on ? "1" : "0");
}
export function getHoldSeconds() {
  const n = parseInt(localStorage.getItem(LS.holdSec) || "5", 10);
  return [3, 5, 10].includes(n) ? n : 5;
}
export function setHoldSeconds(n) {
  localStorage.setItem(LS.holdSec, String([3, 5, 10].includes(n) ? n : 5));
}
export function scaleCapabilities() {
  return {
    ble:    typeof navigator !== "undefined" && !!navigator.bluetooth,
    serial: typeof navigator !== "undefined" && !!navigator.serial,
    hid:    typeof navigator !== "undefined" && !!navigator.hid,
    secure: typeof location !== "undefined"
              && (location.protocol === "https:" || location.hostname === "localhost"),
  };
}
export function whyScaleUnavailable() {
  const c = scaleCapabilities();
  if (!c.secure) return "Weight scale needs a secure (https://) page.";
  if (!c.ble && !c.serial && !c.hid) {
    if (/iP(hone|ad|od)/.test(navigator.userAgent)) {
      return "iOS Safari cannot talk to Bluetooth / USB devices. Use an Android phone or a Chrome/Edge laptop.";
    }
    return "This browser doesn't support Web Bluetooth / Web Serial / Web HID. Use Chrome or Edge.";
  }
  return null;
}

// -------------------- ASCII weight parser -----------------------
/**
 * Universal ASCII parser. Handles common outputs from cheap counter
 * scales and BLE-UART bridges:
 *
 *   "+00.150 kg\r\n"
 *   "ST,GS,+00.150kg\r\n"       (Sartorius / A&D style)
 *   "  1.234 kg S\r\n"
 *   "W 001.234 kg\r\n"
 *   "1.234"                     (bare number, assume kg)
 *
 * Returns { kg, grams, unit, stable, raw } or null when nothing
 * numeric could be found.
 */
export function parseAsciiWeight(raw) {
  const line = String(raw || "").trim();
  if (!line) return null;
  // ST=stable, US=unstable (A&D/OHAUS/Sartorius convention)
  const stable = /\bS(T|TABLE)\b/i.test(line) || /(^|,)S(,|$)/i.test(line);
  const unstable = /\bUS(?=,|\b)|\bU(?=,|\b)/i.test(line);
  const m = line.match(/([-+])?\s*(\d+(?:[.,]\d+)?)\s*(kg|g|lb|oz)?/i);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  const magnitude = parseFloat(m[2].replace(",", "."));
  if (Number.isNaN(magnitude)) return null;
  const unitRaw = (m[3] || "kg").toLowerCase();
  let kg;
  switch (unitRaw) {
    case "g":  kg = magnitude / 1000; break;
    case "lb": kg = magnitude * 0.45359237; break;
    case "oz": kg = magnitude * 0.028349523125; break;
    default:   kg = magnitude;
  }
  kg *= sign;
  return {
    kg: +kg.toFixed(3),
    grams: Math.round(kg * 1000),
    unit: unitRaw,
    stable: stable && !unstable,
    raw: line,
  };
}

/**
 * SIG Weight Measurement (0x2A9D) parser.
 *  - Byte 0: Flags  (bit 0: 0=SI kg, 1=Imperial lb)
 *  - Bytes 1..2: unsigned int LE, resolution 0.005 kg (SI) or 0.01 lb (imp).
 */
function parseSigWeight(dv) {
  if (!dv || dv.byteLength < 3) return null;
  const flags = dv.getUint8(0);
  const imperial = !!(flags & 0x01);
  const raw = dv.getUint16(1, true);
  const kg = imperial ? raw * 0.01 * 0.45359237 : raw * 0.005;
  return {
    kg: +kg.toFixed(3),
    grams: Math.round(kg * 1000),
    unit: imperial ? "lb" : "kg",
    stable: true, // SIG spec only reports final stable value
    raw: `flags=0x${flags.toString(16)} raw=${raw}`,
  };
}

// -------------------- connection state --------------------------
let activeStop = null;
let activeBleDev = null;

/** Force-drop any live subscription. Safe to call multiple times. */
export async function disconnectScale() {
  try { if (activeStop) activeStop(); } catch (_) { /* noop */ }
  activeStop = null;
  try {
    if (activeBleDev?.gatt?.connected) activeBleDev.gatt.disconnect();
  } catch (_) { /* noop */ }
  activeBleDev = null;
}

// -------------------- BLE transport -----------------------------
/**
 * Given a connected GATT server, walk services + chars and return
 * the best notify characteristic for weight reading. Shared by both
 * the fresh-pair path and the silent-reconnect path.
 */
async function _pickNotifyChar(server) {
  // 1. Try the standard weight scale characteristic first.
  try {
    const svc = await server.getPrimaryService(WEIGHT_SERVICE_UUID);
    const ch = await svc.getCharacteristic(WEIGHT_CHAR_UUID);
    return { characteristic: ch, sig: true };
  } catch (_) { /* fall through */ }
  // 2. Otherwise pick the first NOTIFY characteristic anywhere.
  const services = await server.getPrimaryServices();
  for (const svc of services) {
    const chars = await svc.getCharacteristics();
    for (const ch of chars) {
      if (ch.properties.notify || ch.properties.indicate) {
        return { characteristic: ch, sig: false };
      }
    }
  }
  throw new Error("This device has no notify characteristic — is it really a weighing scale?");
}

/**
 * Iter 86 — try to silently reconnect to a previously-granted BLE
 * scale using `navigator.bluetooth.getDevices()` (Chrome 85+). Returns
 * the device+characteristic on success, or `null` if no cached device
 * is available (caller should then fall back to the pair dialog).
 * Never throws — always returns null on any error so the caller can
 * gracefully fall through to a fresh pair.
 */
async function _tryReconnectBle(status) {
  if (!navigator.bluetooth.getDevices) return null;
  try {
    const devs = await navigator.bluetooth.getDevices();
    if (!devs.length) return null;
    const lastName = lastDeviceLabel();
    const target = devs.find(d => d.name === lastName) || devs[0];
    if (!target) return null;
    status?.(`Reconnecting to ${target.name || "scale"}…`);
    const server = await target.gatt.connect();
    const { characteristic, sig } = await _pickNotifyChar(server);
    return { device: target, characteristic, sig };
  } catch (_) { return null; }
}

async function connectBle(status) {
  // Try silent reconnect first — no picker if we already have a device.
  const cached = await _tryReconnectBle(status);
  if (cached) {
    activeBleDev = cached.device;
    cached.device.addEventListener("gattserverdisconnected", () => { /* noop */ });
    return cached;
  }
  // Fresh pair.
  status?.("Choose your BLE scale…");
  const device = await navigator.bluetooth.requestDevice({
    // Accept any device — many cheap scales don't advertise 0x181D
    // in the discovery packet.
    acceptAllDevices: true,
    optionalServices: [WEIGHT_SERVICE_UUID, ...EXTRA_SCALE_SERVICES],
  });
  if (!device) throw new Error("No scale selected.");
  activeBleDev = device;
  device.addEventListener("gattserverdisconnected", () => { /* noop */ });
  status?.("Connecting…");
  const server = await device.gatt.connect();
  const { characteristic, sig } = await _pickNotifyChar(server);
  return { device, characteristic, sig };
}

async function startBleStream({ onReading, onStatus }) {
  const { device, characteristic, sig } = await connectBle(onStatus);
  onStatus?.(`Reading from ${device.name || "BLE scale"}…`);
  const handler = (ev) => {
    const dv = ev.target.value;
    let reading = null;
    if (sig) reading = parseSigWeight(dv);
    if (!reading) {
      const bytes = new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength);
      const txt = safeDecode(bytes);
      reading = parseAsciiWeight(txt);
    }
    if (reading) onReading(reading);
  };
  characteristic.addEventListener("characteristicvaluechanged", handler);
  await characteristic.startNotifications();
  localStorage.setItem(LS.transport, "ble");
  localStorage.setItem(LS.device, device.name || "BLE scale");
  const stop = async () => {
    try { characteristic.removeEventListener("characteristicvaluechanged", handler); } catch(_) {}
    try { await characteristic.stopNotifications(); } catch(_) {}
    try { device.gatt.disconnect(); } catch(_) {}
  };
  return stop;
}

function safeDecode(bytes) {
  try { return dec.decode(bytes); } catch(_) { return ""; }
}

// -------------------- Serial (wired) transport ------------------
/** Iter 86 — try silent reconnect to a previously-granted serial port. */
async function _tryReconnectSerial() {
  if (!navigator.serial?.getPorts) return null;
  try {
    const ports = await navigator.serial.getPorts();
    return ports.length ? ports[0] : null;
  } catch (_) { return null; }
}

async function startSerialStream({ onReading, onStatus }) {
  let port = await _tryReconnectSerial();
  if (!port) {
    onStatus?.("Choose your USB-serial port…");
    port = await navigator.serial.requestPort({});
  } else {
    onStatus?.("Reconnecting to saved port…");
  }
  onStatus?.("Opening 9600 8N1…");
  await port.open({ baudRate: 9600 });
  const reader = port.readable
    .pipeThrough(new TextDecoderStream("utf-8", { fatal: false }))
    .getReader();
  let cancelled = false;
  let buffer = "";
  onStatus?.("Reading…");
  localStorage.setItem(LS.transport, "serial");
  localStorage.setItem(LS.device, "USB-Serial scale");
  (async () => {
    while (!cancelled) {
      try {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        buffer += value;
        // Break on CR or LF
        let idx;
        while ((idx = buffer.search(/[\r\n]/)) !== -1) {
          const line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (!line.trim()) continue;
          const r = parseAsciiWeight(line);
          if (r) onReading(r);
        }
      } catch (_) { break; }
    }
  })();
  return async () => {
    cancelled = true;
    try { await reader.cancel(); } catch(_) {}
    try { reader.releaseLock(); } catch(_) {}
    try { await port.close(); } catch(_) {}
  };
}

// -------------------- HID transport (best effort) ---------------
/** Iter 86 — try silent reconnect to a previously-granted HID scale. */
async function _tryReconnectHid() {
  if (!navigator.hid?.getDevices) return null;
  try {
    const devs = await navigator.hid.getDevices();
    // Prefer devices that advertise the HID Scale usage-page.
    const scale = devs.find(d =>
      (d.collections || []).some(c => c.usagePage === 0x8d),
    ) || devs[0];
    return scale || null;
  } catch (_) { return null; }
}

async function startHidStream({ onReading, onStatus }) {
  let device = await _tryReconnectHid();
  if (!device) {
    onStatus?.("Choose your USB HID scale…");
    const [chosen] = await navigator.hid.requestDevice({
      filters: [{ usagePage: 0x8d }],   // HID Scale
    });
    device = chosen;
  } else {
    onStatus?.(`Reconnecting to ${device.productName || "HID scale"}…`);
  }
  if (!device) throw new Error("No scale selected.");
  if (!device.opened) await device.open();
  onStatus?.(`Reading from ${device.productName || "HID scale"}…`);
  localStorage.setItem(LS.transport, "hid");
  localStorage.setItem(LS.device, device.productName || "HID scale");
  const handler = (ev) => {
    const d = ev.data;
    // Standard HID Scale Data Report:
    // byte 0 report id, byte 1 status (1=fault, 2=stable, 3=in-motion, 4=under, 5=over-zero),
    // byte 2 unit (2=g, 3=kg, 11=oz, 12=lb), byte 3 signed exponent,
    // bytes 4-5 uint16 LE data.
    if (d.byteLength < 6) return;
    const status = d.getUint8(1);
    const unit   = d.getUint8(2);
    const exp    = d.getInt8(3);
    const raw    = d.getUint16(4, true);
    const value  = raw * Math.pow(10, exp);
    let kg = value;
    if (unit === 2)      kg = value / 1000;                    // g → kg
    else if (unit === 11) kg = value * 0.028349523125;          // oz → kg
    else if (unit === 12) kg = value * 0.45359237;              // lb → kg
    // status 2 == stable
    onReading({
      kg: +kg.toFixed(3),
      grams: Math.round(kg * 1000),
      unit: unit === 2 ? "g" : unit === 3 ? "kg" : unit === 11 ? "oz" : "lb",
      stable: status === 2,
      raw: `hid status=${status} unit=${unit} exp=${exp} raw=${raw}`,
    });
  };
  device.addEventListener("inputreport", handler);
  return async () => {
    try { device.removeEventListener("inputreport", handler); } catch(_) {}
    try { await device.close(); } catch(_) {}
  };
}

// -------------------- Public transport dispatcher ---------------
/**
 * Kick off a live weight stream. Auto-picks the best available
 * transport when `transport === "auto"`.
 *
 * Returns a `stop()` function (async).
 */
export async function startScaleStream({
  transport = "auto",
  onReading,
  onStatus,
} = {}) {
  const why = whyScaleUnavailable();
  if (why) throw new Error(why);
  const caps = scaleCapabilities();
  const pick = transport === "auto"
    ? (lastTransport() ||
       (caps.ble    ? "ble"    :
        caps.serial ? "serial" :
        caps.hid    ? "hid"    : ""))
    : transport;
  if (!pick) throw new Error("No supported transport on this device.");
  if (pick === "ble"    && !caps.ble)    throw new Error("Web Bluetooth not available.");
  if (pick === "serial" && !caps.serial) throw new Error("Web Serial not available.");
  if (pick === "hid"    && !caps.hid)    throw new Error("Web HID not available.");
  const args = { onReading, onStatus };
  const stop = pick === "ble"    ? await startBleStream(args)
             : pick === "serial" ? await startSerialStream(args)
             :                     await startHidStream(args);
  activeStop = stop;
  return stop;
}

/** Alias — same as `startScaleStream({transport, onReading, ...})`
 *  but throws if the transport isn't available. Used by ScaleSettings
 *  for the "Pair" buttons. */
export async function pairScale(transport, cbs = {}) {
  return startScaleStream({ transport, ...cbs });
}
